import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import {
  AvisoEducacional,
  CAMPO_SIM,
  Caixa,
  Campo,
  Cartao,
  Dica,
  ExplicacaoDaEtapa,
  Navegar,
  CabineDaRotina,
  PopupDaEtapa,
  Resumo,
  dicasDesligadas,
} from "../componentes/SimuladorMeiUi";
import { ETAPAS_ABERTURA } from "../componentes/SimuladorMeiTextos";
import { filtrar, listarOcupacoes, type Ocupacao } from "../lib/ocupacoes";
import { buscarCep } from "../lib/cep";
import {
  mascararCep,
  mascararCnae,
  mascararCpf,
  mascararInteiro,
  mascararMoeda,
  mascararTelefone,
  soDigitos,
} from "../lib/mascaras";
import {
  dataBr,
  dataHoraBr,
  gerarCnpjFicticio,
  gerarCodigoAutenticacao,
} from "../lib/simuladorMei";
import { htmlDoCcmei } from "../lib/documentosMei";

// A simulação da abertura do CNPJ MEI: gov.br, cinco etapas e o CCMEI.
//
// ---------------------------------------------------------------------------
// A lista de atividades é a do HUB, e mostra também as VEDADAS
// ---------------------------------------------------------------------------
// O app de origem carregava 434 ocupações num `.ts` extraído de PDF — com o
// código CNAE vazado para dentro do nome em várias linhas — e **nenhuma
// vedada**. Aqui a fonte é `mei_ocupacoes` (502 linhas, mantida pela equipe), e
// as 31 vedadas APARECEM na busca, marcadas e não selecionáveis, com a norma que
// as excluiu.
//
// Esconder as vedadas seria mais fácil e ensinaria menos: o aluno que procura
// "despachante" precisa descobrir que não pode ser MEI e POR QUE — não que a
// atividade "não existe". É a mesma decisão da tela de ocupações do MEI PRO, que
// junta as duas listas numa busca só.
//
// ---------------------------------------------------------------------------
// O município vem do ViaCEP, não de uma lista de dez UFs
// ---------------------------------------------------------------------------
// O original tinha `MUNICIPIOS_POR_UF` com dez estados e cinco cidades cada — um
// aluno do Acre não achava a própria cidade. `lib/cep.ts` já resolve isso com
// dado real, falha inofensiva e timeout curto; o campo continua editável, porque
// o ViaCEP não é autoridade sobre onde a pessoa mora.

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR",
  "PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const TIPOS_LOGRADOURO = [
  "Rua","Avenida","Travessa","Alameda","Praça","Rodovia","Estrada","Vila","Largo",
];

const FORMAS_ATUACAO = [
  "Estabelecimento fixo",
  "Internet",
  "Em local fixo fora da loja",
  "Correio",
  "Porta a porta, postos móveis ou por ambulantes",
  "Televenda",
  "Máquinas automáticas",
];

/** Os quatro CNAEs do transportador autônomo de carga (o MEI caminhoneiro). */
const CNAES_CAMINHONEIRO = ["4930-2/01", "4930-2/02", "4930-2/03", "4930-2/04"];

const DECLARACOES: { chave: string; bloco: string; texto: string }[] = [
  {
    chave: "desimpedimento",
    bloco: "Declaração de desimpedimento",
    texto:
      "Declaro, sob as penas da Lei, ser capaz, não estar impedido de exercer atividade " +
      "empresária e que não possuo outro registro de empresário.",
  },
  {
    chave: "simples",
    bloco: "Declaração de opção pelo Simples Nacional",
    texto:
      "Declaro que opto pelo Simples Nacional e pelo SIMEI (arts. 12 e 18-A da Lei " +
      "Complementar nº 123/06), e que não incorro em quaisquer das situações impeditivas a " +
      "essas opções (arts. 3º, 17, 18-A e 29 da mesma lei).",
  },
  {
    chave: "me",
    bloco: "Declaração de enquadramento como Microempresa (ME)",
    texto:
      "Declaro, sob as penas da Lei, que me enquadro na condição de MICROEMPRESA, nos termos " +
      "da Lei Complementar nº 123, de 14/12/2006.",
  },
  {
    chave: "alvara",
    bloco: "Termo de ciência e responsabilidade com efeito de dispensa de alvará e licença",
    texto:
      "Declaro, sob as penas da lei, que conheço e atendo os requisitos legais exigidos pelo " +
      "Estado e pela Prefeitura do Município para a dispensa da emissão do Alvará e Licença de " +
      "Funcionamento, compreendidos os aspectos sanitários, ambientais, tributários, de " +
      "segurança pública, uso e ocupação do solo, atividades domiciliares e restrições ao uso " +
      "de espaços públicos.",
  },
  {
    chave: "inspecao",
    bloco: "Termo de ciência e responsabilidade com efeito de dispensa de alvará e licença",
    texto:
      "Autorizo a realização de inspeção e fiscalização no local de exercício das atividades " +
      "para fins de verificação da observância dos referidos requisitos.",
  },
  {
    chave: "cancelamento",
    bloco: "Termo de ciência e responsabilidade com efeito de dispensa de alvará e licença",
    texto:
      "Declaro, sob as penas da lei, ter ciência de que o não atendimento dos requisitos " +
      "legais exigidos poderá acarretar o cancelamento deste Termo de Ciência e " +
      "Responsabilidade com Efeito de Dispensa de Alvará e Licença de Funcionamento.",
  },
  {
    chave: "publicos",
    bloco: "Termo de ciência e responsabilidade com efeito de dispensa de alvará e licença",
    texto: "Declaro, nos termos da Lei nº 8.934/1994, ter ciência de que dados empresariais são públicos.",
  },
];

type Atividade = { atividade: string; cnae: string | null; icms: boolean | null; iss: boolean | null; descricao: string | null };

type Endereco = {
  cep: string;
  tipoLogradouro: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  referencia: string;
};

const ENDERECO_VAZIO: Endereco = {
  cep: "", tipoLogradouro: "", logradouro: "", numero: "", complemento: "",
  bairro: "", municipio: "", uf: "", referencia: "",
};

type Formulario = {
  nome: string;
  cpf: string;
  nascimento: string;
  nacionalidade: string;
  sexo: string;
  mae: string;
  identidade: string;
  orgaoEmissor: string;
  ufEmissor: string;
  telefone: string;
  email: string;
  capitalSocial: string;
  caminhoneiro: boolean;
  principal: Atividade | null;
  cnaePrincipal: string;
  secundarias: Atividade[];
  formasAtuacao: string[];
  comercial: Endereco;
  residencialIgual: boolean;
  residencial: Endereco;
  aceitas: Record<string, boolean>;
};

const VAZIO: Formulario = {
  nome: "", cpf: "", nascimento: "", nacionalidade: "Brasileira", sexo: "", mae: "",
  identidade: "", orgaoEmissor: "", ufEmissor: "", telefone: "", email: "",
  capitalSocial: "", caminhoneiro: false, principal: null, cnaePrincipal: "",
  secundarias: [], formasAtuacao: [], comercial: ENDERECO_VAZIO,
  residencialIgual: false, residencial: ENDERECO_VAZIO, aceitas: {},
};

export default function SimuladorAbertura() {
  const [lista, setLista] = useState<Ocupacao[] | null>(null);
  const [erroLista, setErroLista] = useState(false);
  const [entrou, setEntrou] = useState(false);
  const [etapa, setEtapa] = useState(1);
  const [form, setForm] = useState<Formulario>(VAZIO);
  const [pronto, setPronto] = useState<{ cnpj: string; data: string } | null>(null);

  // O popup didático: aparece uma vez por etapa, e só se o aluno não desligou.
  const [semDicas] = useState(dicasDesligadas);
  const [popup, setPopup] = useState<number | null>(null);
  const [jaMostradas, setJaMostradas] = useState<number[]>([]);

  const etapaDoTexto = pronto ? 6 : entrou ? etapa : 0;

  useEffect(() => {
    if (semDicas) return;
    if (jaMostradas.includes(etapaDoTexto)) return;
    setPopup(etapaDoTexto);
    setJaMostradas((v) => [...v, etapaDoTexto]);
  }, [etapaDoTexto, semDicas, jaMostradas]);

  useEffect(() => {
    void (async () => {
      const r = await listarOcupacoes();
      if (r === null) setErroLista(true);
      else setLista(r);
    })();
  }, []);

  // Recarregar perde tudo, e o aluno preencheu treze campos. Avisar é o mínimo
  // — o estado é efêmero por decisão, mas perder sem aviso é outra coisa.
  useEffect(() => {
    if (!entrou || pronto) return;
    const aviso = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", aviso);
    return () => window.removeEventListener("beforeunload", aviso);
  }, [entrou, pronto]);

  if (erroLista) {
    return (
      <Moldura>
        <p className="surface p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar a lista de atividades do MEI agora, e sem ela a etapa de
          atividade econômica não funciona. Recarregue a página; se continuar, fale com o
          suporte.
        </p>
      </Moldura>
    );
  }
  if (!lista) return <Carregando />;

  const texto = ETAPAS_ABERTURA[etapaDoTexto];

  if (pronto) {
    return (
      <Moldura>
        <Sucesso cnpj={pronto.cnpj} data={pronto.data} form={form} />
        <ExplicacaoDaEtapa texto={ETAPAS_ABERTURA[6]} />
        {popup !== null && (
          <PopupDaEtapa texto={ETAPAS_ABERTURA[6]} passo={0} total={5} aoFechar={() => setPopup(null)} />
        )}
      </Moldura>
    );
  }

  if (!entrou) {
    return (
      <Moldura>
        <LoginGovBr aoEntrar={() => setEntrou(true)} />
        <ExplicacaoDaEtapa texto={ETAPAS_ABERTURA[0]} />
        {popup !== null && (
          <PopupDaEtapa texto={ETAPAS_ABERTURA[0]} passo={0} total={5} aoFechar={() => setPopup(null)} />
        )}
      </Moldura>
    );
  }

  return (
    <Moldura
      passos={["Identificação", "Atividade", "Endereço", "Declarações", "Confirmação"]}
      atual={etapa}
      // O herói da abertura é o NOME EMPRESARIAL sendo formado — o MEI não
      // escolhe nome de fantasia, ele é "<nome civil> <CPF>", e ver isso aparecer
      // enquanto se digita é a coisa mais didática desta rotina. Sem nome ainda,
      // nenhum cartão: um herói vazio ensina menos que a ausência dele.
      heroi={
        form.nome.trim()
          ? {
              rotulo: "Nome empresarial",
              valor: form.nome.trim().toUpperCase(),
              apoio: "O MEI usa o nome civil do empresário, seguido do CPF",
            }
          : null
      }
    >

      {etapa === 1 && <Identificacao form={form} setForm={setForm} aoAvancar={() => setEtapa(2)} />}
      {etapa === 2 && (
        <AtividadeEconomica
          form={form}
          setForm={setForm}
          lista={lista}
          aoVoltar={() => setEtapa(1)}
          aoAvancar={() => setEtapa(3)}
        />
      )}
      {etapa === 3 && (
        <Enderecos form={form} setForm={setForm} aoVoltar={() => setEtapa(2)} aoAvancar={() => setEtapa(4)} />
      )}
      {etapa === 4 && (
        <Declaracoes form={form} setForm={setForm} aoVoltar={() => setEtapa(3)} aoAvancar={() => setEtapa(5)} />
      )}
      {etapa === 5 && (
        <Confirmacao
          form={form}
          aoVoltar={() => setEtapa(4)}
          aoConcluir={() => setPronto({ cnpj: gerarCnpjFicticio(), data: dataBr(new Date()) })}
        />
      )}

      {texto && <ExplicacaoDaEtapa texto={texto} />}
      {popup !== null && texto && (
        <PopupDaEtapa texto={texto} passo={etapa} total={5} aoFechar={() => setPopup(null)} />
      )}
    </Moldura>
  );
}

function Moldura({
  passos,
  atual,
  heroi,
  children,
}: {
  passos?: string[];
  atual?: number;
  heroi?: { rotulo: string; valor: string; apoio?: string } | null;
  children: React.ReactNode;
}) {
  return (
    <main className="mx-auto max-w-4xl px-4 py-7 sm:px-6">
      <CabineDaRotina
        rotulo="Abertura"
        titulo="Abertura do CNPJ MEI"
        descricao="O caminho do Portal do Empreendedor, do acesso gov.br à emissão do CCMEI."
        passos={passos}
        atual={atual}
        heroi={heroi}
      />
      <div className="mt-6">{children}</div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// gov.br
// ---------------------------------------------------------------------------

/**
 * A tela de acesso do gov.br.
 *
 * O campo de senha é `type="text"`, e isso é deliberado. O app de origem usava
 * `type="password"`: uma tela que imita o gov.br, com os pontinhos no lugar
 * certo, treina o aluno a digitar a senha do gov.br numa página que não é o
 * gov.br. Alguns vão digitar a real. Com o texto à vista, quem começa a digitar
 * a senha verdadeira VÊ e para — e o aviso ao lado diz o resto.
 *
 * Manter o campo (em vez de removê-lo) é o que preserva o valor didático: o
 * fluxo real pede CPF e senha, e o aluno precisa reconhecer essa tela.
 */
function LoginGovBr({ aoEntrar }: { aoEntrar: () => void }) {
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const podeEntrar = soDigitos(cpf).length === 11 && senha.trim().length >= 4;

  return (
    <Cartao>
      <div className="mx-auto max-w-md">
        <div className="mb-6 text-center">
          <span className="inline-block rounded bg-azul px-3 py-1 font-display text-2xl font-bold tracking-tight text-white">
            gov<span className="text-laranja">.br</span>
          </span>
          <h2 className="font-display mt-4 text-lg font-bold text-titulo">Acesse sua conta</h2>
          <p className="text-xs text-cinza">Identifique-se no gov.br</p>
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (podeEntrar) aoEntrar();
          }}
        >
          <Campo rotulo="Número do CPF">
            <input
              className={CAMPO_SIM}
              inputMode="numeric"
              autoComplete="off"
              value={cpf}
              onChange={(e) => setCpf(mascararCpf(e.target.value))}
              placeholder="000.000.000-00"
            />
          </Campo>

          <Campo rotulo="Senha (simulada)">
            <input
              type="text"
              className={CAMPO_SIM}
              autoComplete="off"
              spellCheck={false}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="qualquer palavra com 4 letras"
            />
          </Campo>

          <p className="rounded-xl border-l-4 border-erro-tinta bg-erro-fundo px-3 py-2.5 text-xs leading-relaxed text-erro-tinta">
            <strong>Não digite a sua senha real do gov.br.</strong> Este campo mostra o que você
            escreve justamente para deixar isso claro: ele não valida nada, não envia nada e
            aceita qualquer palavra. Nenhuma ferramenta legítima pede a sua senha de outro
            serviço.
          </p>

          <button type="submit" disabled={!podeEntrar} className="pill-cta w-full text-sm disabled:opacity-50">
            Entrar
          </button>
        </form>

        <p className="mt-5 text-xs leading-relaxed text-cinza">
          Ambiente de demonstração do processo de acesso: informe qualquer CPF de 11 dígitos e
          qualquer senha. Nenhuma autenticação real é realizada.
        </p>
      </div>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Etapa 1 — identificação
// ---------------------------------------------------------------------------

function Identificacao({
  form,
  setForm,
  aoAvancar,
}: {
  form: Formulario;
  setForm: React.Dispatch<React.SetStateAction<Formulario>>;
  aoAvancar: () => void;
}) {
  const trocar = <C extends keyof Formulario>(campo: C, valor: Formulario[C]) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  // O nome empresarial do MEI é os 8 primeiros dígitos do CNPJ + o nome. O CNPJ
  // só existe no fim, então aqui aparece o gabarito — como no portal real.
  const nomeEmpresarial = form.nome.trim()
    ? `NN.NNN.NNN ${form.nome.trim().toUpperCase()}`
    : "NN.NNN.NNN";

  return (
    <Cartao>
      <Caixa>
        Observe os campos marcados como <strong>dado público</strong>, nos termos da Lei nº
        8.934/1994. Onde a alteração é permitida, prefira dados profissionais.
      </Caixa>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="CPF" publico>
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={form.cpf}
            onChange={(e) => trocar("cpf", mascararCpf(e.target.value))}
            placeholder="000.000.000-00"
          />
        </Campo>

        <Campo rotulo="Data de nascimento">
          <input
            type="date"
            className={CAMPO_SIM}
            min="1900-01-01"
            max="2100-12-31"
            value={form.nascimento}
            onChange={(e) => trocar("nascimento", e.target.value)}
          />
        </Campo>

        <Campo
          rotulo="Nome empresarial"
          largo
          publico
          ajuda="O nome empresarial do MEI é formado pelos oito dígitos iniciais do CNPJ seguidos do nome (ou nome social) do empresário. Ele não é escolhido: o sistema monta."
        >
          <input className={`${CAMPO_SIM} bg-chip font-medium`} value={nomeEmpresarial} readOnly />
        </Campo>

        <Campo rotulo="Nome do empresário" largo publico>
          <input
            className={CAMPO_SIM}
            value={form.nome}
            onChange={(e) => trocar("nome", e.target.value)}
          />
        </Campo>

        <Campo rotulo="Nacionalidade">
          <select
            className={CAMPO_SIM}
            value={form.nacionalidade}
            onChange={(e) => trocar("nacionalidade", e.target.value)}
          >
            <option value="Brasileira">Brasileira</option>
            <option value="Estrangeira">Estrangeira</option>
          </select>
        </Campo>

        <Campo rotulo="Sexo">
          <select className={CAMPO_SIM} value={form.sexo} onChange={(e) => trocar("sexo", e.target.value)}>
            <option value="">Selecione</option>
            <option value="Feminino">Feminino</option>
            <option value="Masculino">Masculino</option>
          </select>
        </Campo>

        <Campo rotulo="Nome da mãe" largo>
          <input className={CAMPO_SIM} value={form.mae} onChange={(e) => trocar("mae", e.target.value)} />
        </Campo>

        <Campo rotulo="Nº da identidade">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            maxLength={12}
            value={form.identidade}
            onChange={(e) => trocar("identidade", soDigitos(e.target.value).slice(0, 12))}
          />
        </Campo>

        <Campo rotulo="Órgão emissor">
          <input
            className={CAMPO_SIM}
            value={form.orgaoEmissor}
            onChange={(e) => trocar("orgaoEmissor", e.target.value)}
            placeholder="Ex.: SSP"
          />
        </Campo>

        <Campo rotulo="UF do emissor">
          <select
            className={CAMPO_SIM}
            value={form.ufEmissor}
            onChange={(e) => trocar("ufEmissor", e.target.value)}
          >
            <option value="">Selecione</option>
            {UFS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </Campo>

        <Campo rotulo="Telefone para contato" publico>
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={form.telefone}
            onChange={(e) => trocar("telefone", mascararTelefone(e.target.value))}
            placeholder="(00) 00000-0000"
          />
        </Campo>

        <Campo rotulo="E-mail" publico>
          <input
            type="email"
            className={CAMPO_SIM}
            value={form.email}
            onChange={(e) => trocar("email", e.target.value)}
          />
        </Campo>

        <Campo
          rotulo="Capital social (R$)"
          publico
          ajuda="Quanto o dono está colocando na empresa para ela começar a funcionar — em dinheiro, equipamentos, móveis, ferramentas, computador ou estoque."
        >
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={form.capitalSocial}
            onChange={(e) => trocar("capitalSocial", mascararMoeda(e.target.value).texto)}
            placeholder="R$ 0,00"
          />
        </Campo>
      </div>

      <Navegar aoVoltar={null} aoAvancar={aoAvancar} />
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Etapa 2 — atividade econômica
// ---------------------------------------------------------------------------

function AtividadeEconomica({
  form,
  setForm,
  lista,
  aoVoltar,
  aoAvancar,
}: {
  form: Formulario;
  setForm: React.Dispatch<React.SetStateAction<Formulario>>;
  lista: Ocupacao[];
  aoVoltar: () => void;
  aoAvancar: () => void;
}) {
  const [buscaPrincipal, setBuscaPrincipal] = useState("");
  const [buscaSecundaria, setBuscaSecundaria] = useState("");

  // Marcar caminhoneiro restringe a escolha aos quatro CNAEs do transportador
  // autônomo de carga, e derruba o que não couber — a mesma decisão do original,
  // que é a certa: regime e atividade têm de continuar coerentes.
  const trocarCaminhoneiro = (valor: boolean) =>
    setForm((f) => {
      if (!valor) return { ...f, caminhoneiro: false };
      const principalServe = f.principal?.cnae && CNAES_CAMINHONEIRO.includes(f.principal.cnae);
      return {
        ...f,
        caminhoneiro: true,
        principal: principalServe ? f.principal : null,
        cnaePrincipal: principalServe ? f.cnaePrincipal : "",
        secundarias: f.secundarias.filter((s) => s.cnae && CNAES_CAMINHONEIRO.includes(s.cnae)),
      };
    });

  const disponiveis = useMemo(
    () =>
      form.caminhoneiro
        ? lista.filter((o) => o.cnae && CNAES_CAMINHONEIRO.includes(o.cnae))
        : lista,
    [lista, form.caminhoneiro],
  );

  const daOcupacao = (o: Ocupacao): Atividade => ({
    atividade: o.atividade,
    cnae: o.cnae,
    icms: o.icms,
    iss: o.iss,
    descricao: o.descricao,
  });

  const objetoSocial = [form.principal, ...form.secundarias]
    .map((a) => a?.descricao || a?.atividade)
    .filter(Boolean)
    .join("; ");

  return (
    <Cartao>
      <fieldset className="mb-6">
        <legend className="mb-2 text-sm font-bold text-titulo">Regime de tributação</legend>
        <div className="space-y-2">
          {[
            { valor: false, rotulo: "Geral" },
            { valor: true, rotulo: "Transportador autônomo de cargas (MEI Caminhoneiro)" },
          ].map((op) => (
            <label key={String(op.valor)} className="flex cursor-pointer items-center gap-2 text-sm text-tinta">
              <input
                type="radio"
                name="regime"
                className="size-4 accent-laranja"
                checked={form.caminhoneiro === op.valor}
                onChange={() => trocarCaminhoneiro(op.valor)}
              />
              {op.rotulo}
            </label>
          ))}
        </div>
        {form.caminhoneiro && (
          <p className="mt-2 rounded-lg bg-chip px-3 py-2 text-xs leading-relaxed text-tinta">
            O MEI caminhoneiro contribui com <strong>12% do salário mínimo</strong> de INSS (e não
            5%) e tem um teto de faturamento maior. A escolha de atividade fica restrita aos
            quatro CNAEs de transporte autônomo de carga.
          </p>
        )}
      </fieldset>

      <BuscaDeAtividade
        id="ocupacao-principal"
        rotulo="Ocupação principal"
        lista={disponiveis}
        termo={buscaPrincipal}
        aoDigitar={setBuscaPrincipal}
        aoEscolher={(o) => {
          setForm((f) => ({ ...f, principal: daOcupacao(o), cnaePrincipal: o.cnae ?? "" }));
          setBuscaPrincipal("");
        }}
      />
      {form.principal && (
        <Escolhida
          atividade={form.principal}
          aoRemover={() => setForm((f) => ({ ...f, principal: null, cnaePrincipal: "" }))}
        />
      )}

      <h3 className="mt-7 text-sm font-bold text-titulo">
        Ocupações secundárias
        <span className="ml-2 font-normal text-cinza">({form.secundarias.length} de 15)</span>
      </h3>
      <BuscaDeAtividade
        id="ocupacao-secundaria"
        rotulo="Procurar por atividade, CNAE ou descrição"
        lista={disponiveis}
        termo={buscaSecundaria}
        aoDigitar={setBuscaSecundaria}
        aoEscolher={(o) => {
          setForm((f) => {
            if (f.secundarias.length >= 15) return f;
            if (f.secundarias.some((s) => s.cnae === o.cnae && s.atividade === o.atividade)) return f;
            return { ...f, secundarias: [...f.secundarias, daOcupacao(o)] };
          });
          setBuscaSecundaria("");
        }}
      />
      <div className="mt-3 space-y-2">
        {form.secundarias.length === 0 && (
          <p className="text-xs text-cinza">Nenhuma ocupação secundária selecionada.</p>
        )}
        {form.secundarias.map((a, i) => (
          <Escolhida
            key={`${a.cnae}-${a.atividade}`}
            atividade={a}
            aoRemover={() =>
              setForm((f) => ({ ...f, secundarias: f.secundarias.filter((_, j) => j !== i) }))
            }
          />
        ))}
      </div>

      <div className="mt-7 grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Código CNAE principal" publico>
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={form.cnaePrincipal}
            onChange={(e) => setForm((f) => ({ ...f, cnaePrincipal: mascararCnae(e.target.value) }))}
            placeholder="0000-0/00"
          />
        </Campo>
        <Campo
          rotulo="Descrição do objeto"
          largo
          ajuda="Composto automaticamente pela descrição do CNAE principal e dos secundários."
        >
          <textarea
            className={`${CAMPO_SIM} h-auto bg-chip py-2`}
            rows={3}
            value={objetoSocial}
            readOnly
            placeholder="Escolha as atividades acima para o objeto ser montado."
          />
        </Campo>
      </div>

      <fieldset className="mt-7">
        <legend className="mb-2 text-sm font-bold text-titulo">Forma de atuação</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {FORMAS_ATUACAO.map((op) => (
            <label key={op} className="flex cursor-pointer items-start gap-2 text-sm text-tinta">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-laranja"
                checked={form.formasAtuacao.includes(op)}
                onChange={() =>
                  setForm((f) => ({
                    ...f,
                    formasAtuacao: f.formasAtuacao.includes(op)
                      ? f.formasAtuacao.filter((x) => x !== op)
                      : [...f.formasAtuacao, op],
                  }))
                }
              />
              {op}
            </label>
          ))}
        </div>
      </fieldset>

      <Navegar aoVoltar={aoVoltar} aoAvancar={aoAvancar} />
    </Cartao>
  );
}

/**
 * A busca de atividade.
 *
 * As VEDADAS aparecem, marcadas e não selecionáveis, com a norma que as
 * excluiu. Esconder seria mais simples e ensinaria menos: quem procura
 * "despachante" precisa descobrir que não pode ser MEI e por quê — não que a
 * atividade não existe.
 */
function BuscaDeAtividade({
  id,
  rotulo,
  lista,
  termo,
  aoDigitar,
  aoEscolher,
}: {
  id: string;
  rotulo: string;
  lista: Ocupacao[];
  termo: string;
  aoDigitar: (v: string) => void;
  aoEscolher: (o: Ocupacao) => void;
}) {
  const achadas = useMemo(() => {
    if (termo.trim() === "") return [];
    return filtrar(lista, termo).slice(0, 30);
  }, [lista, termo]);

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-semibold text-cinza">
        {rotulo}
        <Dica texto="A lista é a das ocupações previstas na norma do MEI. Atividades vedadas aparecem marcadas, com a norma que as excluiu — não achar nada aqui não significa que a atividade pode ou não pode." />
      </label>
      <input
        id={id}
        className={CAMPO_SIM}
        type="search"
        value={termo}
        onChange={(e) => aoDigitar(e.target.value)}
        placeholder="cabeleireiro, açougueiro, 4930-2/02…"
      />
      {termo.trim() !== "" && (
        <ul className="mt-1.5 max-h-72 space-y-1 overflow-y-auto rounded-xl border border-borda p-1.5">
          {achadas.length === 0 && (
            <li className="px-2 py-2 text-xs text-cinza">
              Nenhuma ocupação encontrada com esse termo.
            </li>
          )}
          {achadas.map((o) => (
            <li key={o.id} data-atividade={o.atividade}>
              {o.vedada ? (
                <div className="rounded-lg bg-erro-fundo px-2.5 py-2 text-xs">
                  <span className="font-semibold text-erro-tinta">{o.atividade}</span>
                  <span className="ml-2 rounded bg-erro-tinta px-1.5 py-0.5 text-[10px] font-bold text-white uppercase">
                    vedada ao MEI
                  </span>
                  <p className="mt-0.5 text-erro-tinta/90">
                    {o.exclusao
                      ? `Excluída por ${o.exclusao}.`
                      : "Esta atividade não é permitida ao MEI."}
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => aoEscolher(o)}
                  className="w-full rounded-lg px-2.5 py-2 text-left text-xs transition hover:bg-chip"
                >
                  <span className="font-semibold text-titulo">{o.atividade}</span>
                  <span className="mt-0.5 block text-cinza">
                    {o.cnae ?? "sem CNAE"} {o.descricao ? `— ${o.descricao}` : ""}
                  </span>
                  <SelosDeTributo icms={o.icms} iss={o.iss} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Os selos de ICMS e ISS da atividade.
 *
 * `null` é "a lista não diz", e não "não paga" — são coisas diferentes, e a
 * tela mostra a diferença em vez de escolher uma. A mesma decisão de
 * `compor()` em `dasMei.ts`, que compara com `=== true`.
 */
function SelosDeTributo({ icms, iss }: { icms: boolean | null; iss: boolean | null }) {
  if (icms === null && iss === null) {
    return <span className="mt-1 inline-block text-[10px] text-cinza">tributos não informados na lista</span>;
  }
  return (
    <span className="mt-1 inline-flex gap-1">
      {icms === true && (
        <span className="rounded bg-tile px-1.5 py-0.5 text-[10px] font-bold text-titulo uppercase">ICMS</span>
      )}
      {iss === true && (
        <span className="rounded bg-tile px-1.5 py-0.5 text-[10px] font-bold text-titulo uppercase">ISS</span>
      )}
      {icms === false && iss === false && (
        <span className="text-[10px] text-cinza">sem ICMS e sem ISS</span>
      )}
    </span>
  );
}

function Escolhida({ atividade, aoRemover }: { atividade: Atividade; aoRemover: () => void }) {
  return (
    <div
      data-escolhida={atividade.atividade}
      className="flex items-start justify-between gap-3 rounded-xl border border-borda bg-superficie-2 px-3 py-2"
    >
      <div className="min-w-0 text-xs">
        <span className="font-semibold text-titulo">{atividade.atividade}</span>
        <span className="mt-0.5 block text-cinza">
          {atividade.cnae ?? "sem CNAE"} {atividade.descricao ? `— ${atividade.descricao}` : ""}
        </span>
        <SelosDeTributo icms={atividade.icms} iss={atividade.iss} />
      </div>
      <button
        type="button"
        onClick={aoRemover}
        title={`Remover ${atividade.atividade}`}
        aria-label={`Remover ${atividade.atividade}`}
        className="shrink-0 rounded-full px-2 py-1 text-xs font-bold text-cinza transition hover:bg-chip hover:text-titulo"
      >
        ✕
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Etapa 3 — endereços
// ---------------------------------------------------------------------------

function Enderecos({
  form,
  setForm,
  aoVoltar,
  aoAvancar,
}: {
  form: Formulario;
  setForm: React.Dispatch<React.SetStateAction<Formulario>>;
  aoVoltar: () => void;
  aoAvancar: () => void;
}) {
  return (
    <Cartao>
      <BlocoDeEndereco
        titulo="Endereço comercial"
        prefixo="comercial"
        publico
        endereco={form.comercial}
        aoTrocar={(e) =>
          setForm((f) => ({
            ...f,
            comercial: e,
            residencial: f.residencialIgual ? e : f.residencial,
          }))
        }
      />

      <h2 className="font-display mt-8 mb-2 text-base font-bold text-titulo">Endereço residencial</h2>
      <label className="mb-4 flex cursor-pointer items-center gap-2 text-sm text-tinta">
        <input
          type="checkbox"
          className="size-4 accent-laranja"
          checked={form.residencialIgual}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              residencialIgual: e.target.checked,
              residencial: e.target.checked ? f.comercial : f.residencial,
            }))
          }
        />
        O endereço residencial é o mesmo do comercial
      </label>

      {!form.residencialIgual && (
        <BlocoDeEndereco
          titulo=""
          prefixo="residencial"
          endereco={form.residencial}
          aoTrocar={(e) => setForm((f) => ({ ...f, residencial: e }))}
        />
      )}

      <Navegar aoVoltar={aoVoltar} aoAvancar={aoAvancar} />
    </Cartao>
  );
}

function BlocoDeEndereco({
  titulo,
  prefixo,
  publico,
  endereco,
  aoTrocar,
}: {
  titulo: string;
  prefixo: string;
  publico?: boolean;
  endereco: Endereco;
  aoTrocar: (e: Endereco) => void;
}) {
  const [buscando, setBuscando] = useState(false);
  const [naoAchou, setNaoAchou] = useState(false);

  const trocar = <C extends keyof Endereco>(campo: C, valor: Endereco[C]) =>
    aoTrocar({ ...endereco, [campo]: valor });

  const procurar = async () => {
    setNaoAchou(false);
    setBuscando(true);
    const achado = await buscarCep(endereco.cep);
    setBuscando(false);
    if (!achado) {
      setNaoAchou(true);
      return;
    }
    // O ViaCEP devolve "Avenida Paulista" num campo só. O formulário do MEI
    // separa tipo e nome, então o primeiro termo é comparado com a lista de
    // tipos — e se não casar, o logradouro inteiro fica no nome, que é o certo:
    // inventar um tipo é pior que deixar o aluno escolher.
    const primeiro = achado.endereco.split(" ")[0] ?? "";
    const tipo = TIPOS_LOGRADOURO.find((t) => t.toLowerCase() === primeiro.toLowerCase()) ?? "";
    aoTrocar({
      ...endereco,
      tipoLogradouro: tipo,
      logradouro: tipo ? achado.endereco.slice(primeiro.length).trim() : achado.endereco,
      bairro: achado.bairro,
      municipio: achado.cidade,
      uf: achado.estado,
    });
  };

  return (
    <>
      {titulo && <h2 className="font-display mb-4 text-base font-bold text-titulo">{titulo}</h2>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="CEP" publico={publico}>
          <div className="flex gap-2">
            <input
              className={CAMPO_SIM}
              inputMode="numeric"
              value={endereco.cep}
              onChange={(e) => {
                trocar("cep", mascararCep(e.target.value));
                setNaoAchou(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void procurar();
                }
              }}
              placeholder="00000-000"
            />
            <button
              type="button"
              onClick={() => void procurar()}
              disabled={buscando}
              className="shrink-0 rounded-xl border border-borda px-3 text-xs font-semibold text-titulo transition hover:bg-chip disabled:opacity-50"
            >
              {buscando ? "Buscando…" : "Buscar"}
            </button>
          </div>
          {naoAchou && (
            <p className="mt-1 text-xs text-cinza">
              Não encontramos esse CEP. Preencha o endereço à mão — nada aqui depende dessa
              busca.
            </p>
          )}
        </Campo>
        <span className="hidden sm:block" />

        <Campo rotulo="Tipo de logradouro" publico={publico}>
          <select
            className={CAMPO_SIM}
            value={endereco.tipoLogradouro}
            onChange={(e) => trocar("tipoLogradouro", e.target.value)}
          >
            <option value="">Selecione</option>
            {TIPOS_LOGRADOURO.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Logradouro" publico={publico}>
          <input
            className={CAMPO_SIM}
            value={endereco.logradouro}
            onChange={(e) => trocar("logradouro", e.target.value)}
          />
        </Campo>
        <Campo rotulo="Número" publico={publico}>
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={endereco.numero}
            onChange={(e) => trocar("numero", mascararInteiro(e.target.value, 6))}
          />
        </Campo>
        <Campo rotulo="Complemento" publico={publico}>
          <input
            className={CAMPO_SIM}
            value={endereco.complemento}
            onChange={(e) => trocar("complemento", e.target.value)}
          />
        </Campo>
        <Campo rotulo="Bairro ou distrito" publico={publico}>
          <input
            className={CAMPO_SIM}
            value={endereco.bairro}
            onChange={(e) => trocar("bairro", e.target.value)}
          />
        </Campo>
        <Campo rotulo="UF" publico={publico}>
          <select className={CAMPO_SIM} value={endereco.uf} onChange={(e) => trocar("uf", e.target.value)}>
            <option value="">Selecione</option>
            {UFS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Município" publico={publico}>
          <input
            className={CAMPO_SIM}
            value={endereco.municipio}
            onChange={(e) => trocar("municipio", e.target.value)}
            placeholder="preenchido pela busca do CEP"
          />
        </Campo>
        <Campo rotulo="Ponto de referência" largo>
          <input
            className={CAMPO_SIM}
            value={endereco.referencia}
            onChange={(e) => trocar("referencia", e.target.value)}
            placeholder={`Próximo a… (${prefixo})`}
          />
        </Campo>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Etapa 4 — declarações
// ---------------------------------------------------------------------------

function Declaracoes({
  form,
  setForm,
  aoVoltar,
  aoAvancar,
}: {
  form: Formulario;
  setForm: React.Dispatch<React.SetStateAction<Formulario>>;
  aoVoltar: () => void;
  aoAvancar: () => void;
}) {
  const todas = DECLARACOES.every((d) => form.aceitas[d.chave]);
  const blocos = [...new Set(DECLARACOES.map((d) => d.bloco))];

  return (
    <Cartao>
      <h2 className="font-display mb-4 text-lg font-bold text-titulo">Declarações</h2>
      <div className="space-y-5">
        {blocos.map((bloco) => (
          <section
            key={bloco}
            role="group"
            aria-labelledby={`bloco-${blocos.indexOf(bloco)}`}
            className="rounded-xl border border-borda bg-superficie-2 p-4"
          >
            {/* `<h3>` comum e não `<legend>`: a especificação posiciona o legend
                SOBRE a borda, e num card arredondado o título sai flutuando fora
                da caixa — visto na tela em 19/08. */}
            <h3 id={`bloco-${blocos.indexOf(bloco)}`} className="mb-3 text-sm font-bold text-titulo">
              {bloco}
            </h3>
            <div className="space-y-3">
              {DECLARACOES.filter((d) => d.bloco === bloco).map((d) => (
                <label
                  key={d.chave}
                  className="flex cursor-pointer items-start gap-2 text-sm leading-relaxed text-tinta select-none"
                >
                  <input
                    type="checkbox"
                    className="mt-1 size-4 shrink-0 accent-laranja"
                    checked={!!form.aceitas[d.chave]}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, aceitas: { ...f.aceitas, [d.chave]: e.target.checked } }))
                    }
                  />
                  {d.texto}
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>

      {!todas && (
        <p className="mt-3 text-xs text-cinza">
          No processo real, todas as declarações são obrigatórias — marque as sete para
          prosseguir.
        </p>
      )}

      <Navegar aoVoltar={aoVoltar} aoAvancar={aoAvancar} rotuloAvancar="Continuar" avancarBloqueado={!todas} />
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Etapa 5 — confirmação
// ---------------------------------------------------------------------------

function linhaDoEndereco(e: Endereco): string {
  return [
    [e.tipoLogradouro, e.logradouro].filter(Boolean).join(" "),
    e.numero && `nº ${e.numero}`,
    e.complemento,
    e.bairro,
    e.municipio && e.uf ? `${e.municipio}/${e.uf}` : e.municipio || e.uf,
    e.cep && `CEP ${e.cep}`,
  ]
    .filter(Boolean)
    .join(", ");
}

function Confirmacao({
  form,
  aoVoltar,
  aoConcluir,
}: {
  form: Formulario;
  aoVoltar: () => void;
  aoConcluir: () => void;
}) {
  return (
    <Cartao>
      <h2 className="font-display mb-4 text-lg font-bold text-titulo">Confirmação dos dados</h2>

      <div className="grid gap-3 md:grid-cols-2">
        <Resumo
          titulo="Identificação"
          itens={[
            ["Nome", form.nome],
            ["CPF", form.cpf],
            ["Nascimento", form.nascimento],
            ["Nacionalidade", form.nacionalidade],
            ["Sexo", form.sexo],
            ["Mãe", form.mae],
            ["Identidade", `${form.identidade} ${form.orgaoEmissor}/${form.ufEmissor}`.trim()],
            ["Telefone", form.telefone],
            ["E-mail", form.email],
            ["Capital social", form.capitalSocial],
          ]}
        />
        <Resumo
          titulo="Endereço comercial"
          itens={[
            ["Endereço", linhaDoEndereco(form.comercial)],
            ["Ponto de referência", form.comercial.referencia],
          ]}
        />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <Resumo
          titulo="Atividade"
          itens={[
            ["Regime", form.caminhoneiro ? "MEI Caminhoneiro" : "Geral"],
            ["Ocupação principal", form.principal?.atividade ?? ""],
            ["CNAE principal", form.cnaePrincipal],
            ["Secundárias", String(form.secundarias.length)],
            ["Forma de atuação", form.formasAtuacao.join(", ")],
          ]}
        />
        <Resumo
          titulo="Endereço residencial"
          itens={[
            ["Igual ao comercial", form.residencialIgual ? "Sim" : "Não"],
            ["Endereço", linhaDoEndereco(form.residencialIgual ? form.comercial : form.residencial)],
          ]}
        />
      </div>

      <AvisoEducacional>
        Nenhum CNPJ real será criado e nenhum dado é enviado a órgãos oficiais. O número gerado a
        seguir é fictício.
      </AvisoEducacional>

      <Navegar aoVoltar={aoVoltar} aoAvancar={aoConcluir} rotuloAvancar="Concluir abertura" />
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Sucesso e CCMEI
// ---------------------------------------------------------------------------

function Sucesso({ cnpj, data, form }: { cnpj: string; data: string; form: Formulario }) {
  const navegar = useNavigate();

  const emitirCcmei = () => {
    // `window.open` SÍNCRONO, dentro do clique: pop-up depois de um `await` é
    // bloqueado, e abrir a aba vazia antes entregaria `window.opener` à página.
    // A mesma decisão da calculadora de honorários.
    const janela = window.open("", "_blank");
    if (!janela) {
      window.alert("Libere as janelas pop-up deste site para emitir o CCMEI.");
      return;
    }
    const agora = new Date();
    janela.document.write(
      htmlDoCcmei({
        cnpj,
        nomeEmpresarial: `${cnpj.replace(/\D/g, "").slice(0, 8)} ${form.nome.toUpperCase()}`.trim(),
        nomeEmpresario: form.nome,
        cpf: form.cpf,
        dataAbertura: data,
        dataEmissao: dataHoraBr(agora),
        capitalSocial: form.capitalSocial.replace(/^R\$\s*/u, ""),
        enderecoComercial: linhaDoEndereco(form.comercial),
        ocupacaoPrincipal: form.principal?.atividade ?? "",
        cnaePrincipal: form.cnaePrincipal,
        descricaoCnaePrincipal: form.principal?.descricao ?? "",
        formasAtuacao: form.formasAtuacao,
        secundarias: form.secundarias.map((s) => ({
          cnae: s.cnae ?? "sem CNAE",
          descricao: s.descricao ?? s.atividade,
        })),
        codigoAutenticacao: gerarCodigoAutenticacao(),
      }),
    );
    janela.document.close();
  };

  return (
    <Cartao>
      <p className="text-sm leading-relaxed text-tinta">
        A inscrição como Microempreendedor Individual foi concluída, e o número do CNPJ é{" "}
        <strong className="text-titulo">{cnpj}</strong>.
      </p>
      <p className="mt-1 text-xs text-cinza">
        Abertura em {data} · Situação cadastral ATIVA · Natureza jurídica 213-5 — Empresário
        (Individual)
      </p>

      <h2 className="font-display mt-6 mb-4 text-xl font-bold text-titulo">Próximos passos</h2>

      <div className="grid gap-4 md:grid-cols-3">
        <ProximoPasso
          titulo="Emita o comprovante de registro"
          descricao="O CCMEI é o documento que certifica que a empresa está registrada no CNPJ e autorizada a funcionar."
          rotulo="Emitir CCMEI"
          aoClicar={emitirCcmei}
        />
        <ProximoPasso
          titulo="Produtos e serviços financeiros"
          descricao="No portal real, o CREDMEI dá acesso a conta PJ, linhas de financiamento e maquininhas para o negócio."
          rotulo="Indisponível na simulação"
        />
        <ProximoPasso
          titulo="Serviços do Simples Nacional"
          descricao="É onde o MEI emite o DAS mensal e acompanha as obrigações. Aqui, abre a apuração do DAS com este CNPJ."
          rotulo="Apurar o DAS"
          aoClicar={() =>
            navegar(`/ferramenta/simulador-mei/das?cnpj=${encodeURIComponent(cnpj)}`)
          }
        />
      </div>

      <AvisoEducacional>
        Este CNPJ é fictício, não existe em cadastro nenhum, e o CCMEI sai com marca d'água e sem
        validade jurídica.
      </AvisoEducacional>
    </Cartao>
  );
}

function ProximoPasso({
  titulo,
  descricao,
  rotulo,
  aoClicar,
}: {
  titulo: string;
  descricao: string;
  rotulo: string;
  aoClicar?: () => void;
}) {
  return (
    <section className="flex flex-col rounded-xl border border-borda p-4">
      <h3 className="font-display text-sm font-bold text-titulo">{titulo}</h3>
      <p className="mt-1 flex-1 text-xs leading-relaxed text-cinza">{descricao}</p>
      <button
        type="button"
        onClick={aoClicar}
        disabled={!aoClicar}
        className="pill-cta mt-3 text-xs disabled:cursor-not-allowed disabled:opacity-40"
      >
        {rotulo}
      </button>
    </section>
  );
}
